import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CaretLeft, CaretRight, ArrowRight } from 'phosphor-react';
import { API_ENDPOINTS } from '../config/api';
import { getImage } from '../assets/images';
import './HomeImageSlider.css';

const HomeImageSlider = () => {
  const navigate = useNavigate();
  const [images, setImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    loadSliderImages();
  }, []);

  // Auto-play slider
  useEffect(() => {
    if (!isAutoPlaying || images.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === images.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000); // Muda a cada 5 segundos

    return () => clearInterval(interval);
  }, [images.length, isAutoPlaying]);

  const loadSliderImages = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.HOME_SLIDER);
      const data = await response.json();
      
      if (data.status === 'ok') {
        setImages(data.images);
        // Se não há imagens no slider, usar imagem padrão
        if (data.images.length === 0) {
          setImages([{
            id: 'default',
            image_url: 'camara.jpg',
            title: 'Sistemas de Segurança',
            description: 'Proteja o que mais importa'
          }]);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar imagens do slider:', error);
      // Fallback para imagem padrão
      setImages([{
        id: 'default',
        image_url: 'camara.jpg',
        title: 'Sistemas de Segurança',
        description: 'Proteja o que mais importa'
      }]);
    } finally {
      setLoading(false);
    }
  };

  const nextSlide = () => {
    if (isTransitioning) return; // Evita transições sobrepostas
    
    setIsTransitioning(true);
    setCurrentIndex((prevIndex) => 
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
    
    // Reset transição após animação completar
    setTimeout(() => {
      setIsTransitioning(false);
    }, 600); // Match com duração da animação CSS
  };

  const prevSlide = () => {
    if (isTransitioning) return; // Evita transições sobrepostas
    
    setIsTransitioning(true);
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
    
    // Reset transição após animação completar
    setTimeout(() => {
      setIsTransitioning(false);
    }, 600); // Match com duração da animação CSS
  };

  const goToSlide = (index) => {
    if (isTransitioning || index === currentIndex) return;
    
    setIsTransitioning(true);
    setCurrentIndex(index);
    
    // Reset transição após animação completar
    setTimeout(() => {
      setIsTransitioning(false);
    }, 600); // Match com duração da animação CSS
  };

  const handleMouseEnter = () => {
    setIsAutoPlaying(false);
  };

  const handleMouseLeave = () => {
    setIsAutoPlaying(true);
  };

  if (loading) {
    return (
      <section className="hero-slider loading">
        <div className="slider-loading">
          <div className="loading-spinner"></div>
        </div>
      </section>
    );
  }

  const currentImage = images[currentIndex];

  return (
    <section 
      className="hero-slider"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="slider-container">
        {/* Container de imagens com transições */}
        <div className="slides-wrapper">
          {images.map((image, index) => (
            <div 
              key={image.id || index}
              className={`slide-image-container ${
                index === currentIndex ? 'active' : ''
              } ${isTransitioning ? 'transitioning' : ''}`}
              style={{
                opacity: index === currentIndex ? 1 : 0,
                zIndex: index === currentIndex ? 2 : 1
              }}
            >
              <img 
                src={getImage(image.image_url)} 
                alt={image.title || 'Slide'}
                className="slide-image"
                onError={(e) => {
                  e.target.src = getImage('camara.jpg');
                }}
              />
              <div className="slide-overlay"></div>
            </div>
          ))}
        </div>

        {/* Conteúdo sobre a imagem */}
        <div className="slide-content">
          <div className="slide-text">
            <h1>
              {currentImage.title || 'Proteja o que mais importa com tecnologia de ponta'}
            </h1>
            <p>
              {currentImage.description || 'Soluções completas em segurança: videovigilância, alarmes e videoporteiros para sua casa ou empresa'}
            </p>
          </div>
          
          {/* Botões de ação */}
          <div className="slide-buttons">
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/orcamento')}
            >
              Pedir Orçamento Grátis
              <ArrowRight size={20} />
            </button>
            <button 
              className="btn btn-secondary"
              onClick={() => navigate('/shop')}
            >
              Ver Produtos
            </button>
          </div>
        </div>

        {/* Navegação (só aparece se houver mais de 1 imagem) */}
        {images.length > 1 && (
          <>
            <button 
              className="slider-nav slider-nav-prev" 
              onClick={prevSlide}
              aria-label="Slide anterior"
            >
              <CaretLeft size={24} weight="bold" />
            </button>
            <button 
              className="slider-nav slider-nav-next" 
              onClick={nextSlide}
              aria-label="Próximo slide"
            >
              <CaretRight size={24} weight="bold" />
            </button>
          </>
        )}

        {/* Indicadores (só aparecem se houver mais de 1 imagem) */}
        {images.length > 1 && (
          <div className="slider-indicators">
            {images.map((_, index) => (
              <button
                key={index}
                className={`indicator ${index === currentIndex ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
                aria-label={`Ir para slide ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default HomeImageSlider;
